const receivedEmptyInput = ({ sensors, samples, aggregationLevel }) => {
  if (!sensors) {
    return true;
  }

  if (Object.keys(sensors).length === 0) {
    return true;
  }

  if (!samples) {
    return true;
  }

  if (Object.keys(samples).length === 0) {
    return true;
  }

  if (aggregationLevel === '') {
    return true;
  }

  return false;
};

const removePrefixNodeName = name =>
  name.startsWith('a_') || name.startsWith('e_') || name.startsWith('g_')
    ? name.substring(2, name.length)
    : name;

const prefixNodeNames = ({ group, name }) => {
  if (group === 'area_functional') {
    return `a_${name}`;
  }

  if (group === 'equipment_type') {
    return `e_${name}`;
  }

  if (group === 'equipment_group') {
    return `g_${name}`;
  }

  return name;
};

const groupToHeadingMapping = group => {
  switch (group) {
    case 'main':
      return 'Summary';
    case 'building_floor':
      return 'Building Floor';
    case 'area_room':
      return 'Room';
    case 'equipment_type':
      return 'Equipment Type';
    case 'equipment_group':
      return 'Equipment Group';
    case 'area_functional':
      return 'Functional Area';
    case 'area_description':
      return 'Area Description';
    default:
      return 'Unknown Headline';
  }
};

const filterExcludedGroups = (excludedGroups, sensor, sensors) => {
  for (let [group, types] of Object.entries(excludedGroups)) {
    if (sensors.hasOwnProperty(sensor.id) === false) {
      continue;
    }

    if (types.includes(sensors[sensor.id][group])) {
      return false;
    }
  }
  return true;
};

const DataToSankeyChart = ({ sensors, samples, aggregationLevel, groups }) => {
  if (receivedEmptyInput({ sensors, samples, aggregationLevel })) {
    return {
      nodes: [],
      links: [],
      headers: [],
    };
  }

  let groupToEnergySumValues = groups.reduce(
    (prev, next) => ({ ...prev, [next]: {} }),
    {}
  );
  let sensorsToGroupMapping = groups.reduce(
    (prev, next) => ({ ...prev, [next]: {} }),
    {}
  );
  let groupToTypesMapping = groups.reduce(
    (prev, next) => ({ ...prev, [next]: {} }),
    {}
  );

  const renderSubDistributionPanelFeed = Object.values(sensors).some(
    s => s.area_functional !== 'Apartment'
  );

  let excludedGroups = {
    type: ['main'],
    equipment_type: [
      renderSubDistributionPanelFeed ? undefined : 'Subdistribution Panel Feed',
    ],
  };

  for (let sensor of Object.values(sensors)) {
    if (filterExcludedGroups(excludedGroups, sensor, sensors) === false) {
      // todo: capture mains separately
      continue;
    }

    groups.forEach(key => {
      const name = prefixNodeNames({ group: key, name: sensor[key] });
      sensorsToGroupMapping[key][sensor.id] = name;
      groupToEnergySumValues[key][name] = 0;
    });
  }

  for (let sampleGroup of Object.values(samples)) {
    sampleGroup.sensors
      .filter(sensor => filterExcludedGroups(excludedGroups, sensor, sensors))
      .forEach(sensorId => {
        const energyValue = sampleGroup.energy[sensorId];
        if (isNaN(energyValue)) {
          return;
        }

        groups.forEach(key => {
          if (sensorsToGroupMapping[key][sensorId]) {
            groupToEnergySumValues[key][
              sensorsToGroupMapping[key][sensorId]
            ] += energyValue;
          }
        });

        groups.forEach((group, index) => {
          const next = index + 1;

          if (next === groups.length) {
            return;
          }

          const nextKey = groups[next];

          const source = sensorsToGroupMapping[group][sensorId];
          const target = sensorsToGroupMapping[nextKey][sensorId];

          if (target === undefined || source === undefined) {
            return;
          }

          if (groupToTypesMapping[group].hasOwnProperty(source) === false) {
            groupToTypesMapping[group][source] = {};
          }

          if (
            groupToTypesMapping[group][source].hasOwnProperty(target) === false
          ) {
            groupToTypesMapping[group][source][target] = 0;
          }

          groupToTypesMapping[group][source][target] += energyValue;
        });
      });
  }

  const flatten = list =>
    list.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), []);

  const getNodes = ({ groups, groupToEnergySumValues }) => {
    let nodeIndexMapping = {};
    let nodeIndex = 0;

    const nodes = groups.map((group, depth) => {
      return [...Object.keys(groupToEnergySumValues[group])].map(name => {
        const node = {
          node: nodeIndex,
          name: removePrefixNodeName(name), // todo: remove a_ and e_
          depth,
          group,
        };

        nodeIndexMapping[name] = nodeIndex;
        nodeIndex++;

        return node;
      });
    });

    return {
      nodes: flatten(nodes),
      nodeIndexMapping,
    };
  };

  const getLinks = ({ groupToTypesMapping, nodeIndexMapping }) => {
    const mapping = Object.keys(groupToTypesMapping).map(group =>
      Object.keys(groupToTypesMapping[group]).map(source =>
        Object.keys(groupToTypesMapping[group][source]).map(target => ({
          // check if target && source are identical?
          source: nodeIndexMapping[source],
          target: nodeIndexMapping[target],
          value: groupToTypesMapping[group][source][target],
        }))
      )
    );

    return {
      links: flatten(mapping),
    };
  };

  const { nodes, nodeIndexMapping } = getNodes({
    groups,
    groupToEnergySumValues,
  });
  const { links } = getLinks({ groupToTypesMapping, nodeIndexMapping });

  return {
    nodes: nodes,
    links: links,
    headers: groups.map(group => groupToHeadingMapping(group)),
  };
};

export default DataToSankeyChart;



// WEBPACK FOOTER //
// ./src/helper/DataToSankeyChart.js